<template>
    <div class="mobile-signature-pad">
        <div class="sig-hint">{{ $t("signature.hint") }}</div>
        <div ref="canvasWrap" class="sig-canvas-wrap">
            <canvas ref="canvas" class="sig-canvas"></canvas>
        </div>
        <div class="sig-actions">
            <b-button class="sig-btn sig-btn-ghost" variant="outline-secondary" @click="clear">
                <i class="el-icon-close"></i>
                {{ $t("signature.clear") }}
            </b-button>
            <b-button class="sig-btn sig-btn-ghost" variant="outline-secondary" @click="undo">
                <i class="el-icon-refresh-left"></i>
                {{ $t("signature.undo") }}
            </b-button>
            <b-button class="sig-btn sig-btn-primary" variant="primary" :disabled="submitting" @click="confirm">
                <i v-if="submitting" class="el-icon-loading"></i>
                {{ $t("signature.confirm") }}
            </b-button>
        </div>
    </div>
</template>

<script>
import SignaturePad from "signature_pad";
import { dataURLtoBlob, uploadEncryptedImage, saveSignature } from "@/utils/signatureApi";

// 笔迹参数：signature_pad 默认 0.5~2.5px 太细，签名图缩进 PDF 箱单后几乎不可见
const PEN_COLOR = "#000000";
const PEN_MIN_WIDTH = 1.5;
const PEN_MAX_WIDTH = 4;

export default {
    name: "MobileSignaturePad",
    props: {
        uuid: { type: String, default: null },
        token: { type: String, default: null }
    },
    data() {
        return {
            pad: null,
            submitting: false
        };
    },
    mounted() {
        this.pad = new SignaturePad(this.$refs.canvas, {
            penColor: PEN_COLOR,
            backgroundColor: "rgba(255,255,255,0)",
            minWidth: PEN_MIN_WIDTH,
            maxWidth: PEN_MAX_WIDTH
        });
        // 内嵌在覆盖层里使用时，容器可能刚从 display:none 切出来，延后一拍再量尺寸
        this.$nextTick(this.resizeCanvas);
        window.addEventListener("resize", this.resizeCanvas);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.resizeCanvas);
        if (this.pad) {
            this.pad.off();
        }
    },
    methods: {
        resizeCanvas() {
            const canvas = this.$refs.canvas;
            const wrap = this.$refs.canvasWrap;
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = wrap.clientWidth * ratio;
            canvas.height = wrap.clientHeight * ratio;
            canvas.style.width = wrap.clientWidth + "px";
            canvas.style.height = wrap.clientHeight + "px";
            canvas.getContext("2d").scale(ratio, ratio);
            this.pad.clear();
        },
        clear() {
            this.pad.clear();
        },
        undo() {
            const data = this.pad.toData();
            if (data && data.length) {
                data.pop();
                this.pad.fromData(data);
            }
        },
        buildVectorData() {
            const canvas = this.$refs.canvas;
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            return JSON.stringify({
                version: 1,
                canvas: {
                    width: Math.round(canvas.width / ratio),
                    height: Math.round(canvas.height / ratio),
                    ratio: ratio
                },
                penColor: PEN_COLOR,
                minWidth: PEN_MIN_WIDTH,
                maxWidth: PEN_MAX_WIDTH,
                strokes: this.pad.toData()
            });
        },
        buildDeviceInfo() {
            return JSON.stringify({
                userAgent: navigator.userAgent,
                platform: navigator.platform,
                screen: window.screen.width + "x" + window.screen.height,
                entry: this.uuid ? "scan" : "inapp"
            });
        },
        async confirm() {
            if (this.pad.isEmpty()) {
                this.$message.warning(this.$t("signature.emptyWarn"));
                return;
            }
            this.submitting = true;
            try {
                const blob = dataURLtoBlob(this.pad.toDataURL("image/png"));
                const attachment = await uploadEncryptedImage(blob);
                const uuid = await saveSignature({
                    signatureUuid: this.uuid || null,
                    token: this.token || null,
                    imageAttachmentId: attachment.id,
                    vectorData: this.buildVectorData(),
                    deviceInfo: this.buildDeviceInfo()
                });
                this.$emit("signed", { uuid: uuid, attachmentId: attachment.id });
            } catch (e) {
                this.$message.error(this.$t("signature.saveFailed"));
            } finally {
                this.submitting = false;
            }
        }
    }
};
</script>

<style scoped>
.mobile-signature-pad {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 12px;
    box-sizing: border-box;
}
.sig-hint {
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
}
.sig-canvas-wrap {
    flex: 1;
    border: 1px dashed #bbb;
    border-radius: 8px;
    background: #fff;
    min-height: 200px;
}
.sig-canvas {
    touch-action: none;
    display: block;
}
.sig-actions {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-top: 12px;
}
.sig-btn {
    flex: 1;
    font-weight: 500;
    border-radius: 8px;
    transition: all 0.2s ease;
}
.sig-btn i {
    margin-right: 4px;
}
.sig-btn-ghost {
    color: #5b6b7a;
    border-color: #d0d7de;
}
.sig-btn-ghost:hover,
.sig-btn-ghost:focus {
    color: #1E5274;
    background-color: #eef4f8;
    border-color: #1E5274;
}
.sig-btn-primary {
    background-color: #1E5274;
    border-color: #1E5274;
    box-shadow: 0 2px 6px rgba(30, 82, 116, 0.25);
}
.sig-btn-primary:hover,
.sig-btn-primary:focus {
    background-color: #17415d;
    border-color: #17415d;
    box-shadow: 0 4px 10px rgba(30, 82, 116, 0.35);
}
.sig-btn-primary:disabled {
    background-color: #7d9cb0;
    border-color: #7d9cb0;
    box-shadow: none;
}
</style>
